import { useNavigate } from "react-router-dom";

import { getSellerPath } from "@/lib/routing/portal-urls";

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { ProductImageView } from "@/lib/api/types";
import { useCreateProductUploadIntentMutation } from "@/modules/products/services/useCreateProductUploadIntentMutation";
import { useUploadToSignedUrlMutation } from "@/shared/services/useUploadToSignedUrlMutation";
import { useUpdateProductImagesMutation } from "@/modules/products/services/useUpdateProductImagesMutation";
import { Alert, AutoDismissNotice } from "@a2z/ui";
import { useFloatingActionBar } from "@/lib/hooks/useFloatingActionBar";
import { useFormSafety } from "@/lib/navigation/useFormSafety";
import { useNavigationGuard } from "@/lib/navigation/navigationGuard";
import { ProductImagesFormActions } from "./product-images/ProductImagesFormActions";
import { ProductImagesHybridPanel } from "./product-images/ProductImagesHybridPanel";
import { ProductImagesViewer } from "./product-images/ProductImagesViewer";
import {
  buildImagesPayload,
  buildImageSignature,
  buildViewerItems,
  createLocalId,
  mergeImagesWithPreviewFallback,
  mapRemoteImages,
  moveImageByDirection,
  moveImageToPosition,
  reconcileSavedImages,
  uploadPendingImages,
  validateImageFile,
  resolveDetailUrl,
  resolvePreviewUrl,
} from "./product-images/helpers";
import type {
  ImageItem,
  UploadedImageMap,
  ViewerDirection,
} from "./product-images/types";

type Props = {
  locale: string;
  storeSlug: string;
  productId: string;
  initialImages?: ProductImageView[];
  submitLabel?: string;
  onSuccess?: () => void;
  onImagesSaved?: (count: number) => void;
  successMessage?: string;
  redirectOnSuccess?: boolean;
  showTips?: boolean;
  maxImages?: number;
  hideCancel?: boolean;
  /** Suppress the inline save/cancel action bar (page-level SaveBar takes over). */
  hideActions?: boolean;
  onCancel?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  enableNavigationGuard?: boolean;
  requestImagePicker?: number;
};

export function ProductImagesForm({
  locale,
  storeSlug,
  productId,
  initialImages,
  submitLabel,
  onSuccess,
  onImagesSaved,
  successMessage,
  redirectOnSuccess = true,
  showTips = false,
  maxImages: maxImagesProp,
  hideCancel = false,
  hideActions = false,
  onCancel,
  onDirtyChange,
  enableNavigationGuard = true,
  requestImagePicker = 0,
}: Props) {
  const navigate = useNavigate();
  const t = useTranslations();
  const errorRef = useRef<HTMLDivElement | null>(null);
  const trackedPreviewUrlsRef = useRef<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const initialMappedImages = useMemo(
    () => mapRemoteImages(initialImages),
    [initialImages],
  );
  const initialMappedSignature = useMemo(
    () => buildImageSignature(initialMappedImages),
    [initialMappedImages],
  );
  const lastAppliedInitialSignatureRef = useRef(initialMappedSignature);
  const [savedImages, setSavedImages] = useState<ImageItem[]>(
    () => initialMappedImages,
  );
  const [imageItems, setImageItems] = useState<ImageItem[]>(
    () => initialMappedImages,
  );
  const formId = useId();
  const formRef = useRef<HTMLFormElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const lastPickerRequestRef = useRef(0);
  const actionRef = useRef<HTMLDivElement | null>(null);

  const orderedImages = useMemo(
    () => [...imageItems].sort((a, b) => a.sortOrder - b.sortOrder),
    [imageItems],
  );
  const savedSignature = useMemo(
    () => buildImageSignature(savedImages),
    [savedImages],
  );
  const currentSignature = useMemo(
    () => buildImageSignature(orderedImages),
    [orderedImages],
  );
  const hasUnsavedChanges = currentSignature !== savedSignature;
  const actionsVisible = hasUnsavedChanges || isSubmitting;

  const resetImageItems = useCallback(() => {
    setImageItems(savedImages);
    formRef.current?.reset();
  }, [savedImages]);

  const { promptDiscard } = useFormSafety({
    isDirty: hasUnsavedChanges,
    onDiscard: () => {
      resetImageItems();
      setShowSuccess(false);
      setError(null);
      setUploadError(null);
      setViewerIndex(null);
      setIsUploading(false);
    },
    enableNavigationGuard,
  });

  const guard = useNavigationGuard();
  const discardPrompt = guard?.promptDiscard ?? promptDiscard;
  const shouldFloatActions = useFloatingActionBar(
    actionRef,
    actionsVisible && !hideCancel,
  );
  const saveLabel = submitLabel ?? t(keys.common.save);
  const { mutateAsync: createProductUploadIntent } =
    useCreateProductUploadIntentMutation();
  const { mutateAsync: uploadToSignedUrl } = useUploadToSignedUrlMutation();
  const { mutateAsync: updateProductImages } = useUpdateProductImagesMutation();

  const handleCancel = () => {
    discardPrompt(() => {
      resetImageItems();
      setShowSuccess(false);
      setError(null);
      setUploadError(null);
      setViewerIndex(null);
      setIsUploading(false);
      if (onCancel) {
        onCancel();
      }
    });
  };

  const [floatingVisible, setFloatingVisible] = useState(false);
  // Hide immediately during render when the bar stops floating; the show
  // transition still waits one frame (async, in the effect) so CSS animates.
  if (!shouldFloatActions && floatingVisible) {
    setFloatingVisible(false);
  }
  useEffect(() => {
    if (!shouldFloatActions) {
      return;
    }
    requestAnimationFrame(() => {
      setFloatingVisible(true);
    });
  }, [shouldFloatActions]);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [error]);

  useEffect(() => {
    onDirtyChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onDirtyChange]);

  useEffect(() => {
    const currentPreviewUrls = new Set(
      imageItems
        .map((item) => item.previewUrl ?? null)
        .filter((previewUrl): previewUrl is string => Boolean(previewUrl)),
    );
    for (const previousUrl of trackedPreviewUrlsRef.current) {
      if (!currentPreviewUrls.has(previousUrl)) {
        URL.revokeObjectURL(previousUrl);
      }
    }
    trackedPreviewUrlsRef.current = currentPreviewUrls;
  }, [imageItems]);

  useEffect(
    () => () => {
      for (const previewUrl of trackedPreviewUrlsRef.current) {
        URL.revokeObjectURL(previewUrl);
      }
      trackedPreviewUrlsRef.current.clear();
    },
    [],
  );

  const maxImages = Math.max(1, maxImagesProp ?? 3);
  const imageLimitReached = imageItems.length >= maxImages;
  const disableReorder = isUploading || isSubmitting;

  useEffect(() => {
    if (hasUnsavedChanges) {
      return;
    }
    if (initialMappedSignature === lastAppliedInitialSignatureRef.current) {
      return;
    }

    lastAppliedInitialSignatureRef.current = initialMappedSignature;
    setSavedImages(initialMappedImages);
    setImageItems(initialMappedImages);
  }, [hasUnsavedChanges, initialMappedImages, initialMappedSignature]);

  useEffect(() => {
    if (requestImagePicker <= 0) {
      return;
    }
    if (requestImagePicker === lastPickerRequestRef.current) {
      return;
    }
    if (isUploading || isSubmitting || imageLimitReached) {
      return;
    }
    lastPickerRequestRef.current = requestImagePicker;
    fileInputRef.current?.click();
  }, [imageLimitReached, isSubmitting, isUploading, requestImagePicker]);

  const viewerItems = useMemo(
    () => buildViewerItems(orderedImages),
    [orderedImages],
  );

  const cycleViewer = useCallback(
    (direction: ViewerDirection) => {
      setViewerIndex((current) => {
        if (current === null || viewerItems.length === 0) {
          return null;
        }
        if (direction === "prev") {
          return (current - 1 + viewerItems.length) % viewerItems.length;
        }
        return (current + 1) % viewerItems.length;
      });
    },
    [viewerItems.length],
  );

  const activeViewerUrl =
    viewerIndex !== null ? (viewerItems[viewerIndex]?.url ?? null) : null;

  // Compute viewer BG removal data for the active image
  const activeViewerBgRemoval = useMemo(() => {
    if (viewerIndex === null) return null;
    const viewerItem = viewerItems[viewerIndex];
    if (!viewerItem) return null;
    const activeImage = orderedImages.find((img) => img.id === viewerItem.id);
    if (!activeImage?.bgRemoval) {
      return null;
    }
    const originalUrl =
      resolveDetailUrl({ ...activeImage, bgRemoval: undefined }) ??
      resolvePreviewUrl({ ...activeImage, bgRemoval: undefined });
    const cleanUrl =
      activeImage.bgRemoval.cleanDetailUrl ??
      activeImage.bgRemoval.cleanCardUrl ??
      null;
    if (!originalUrl || !cleanUrl) return null;
    return { originalUrl, cleanUrl };
  }, [viewerIndex, viewerItems, orderedImages]);

  const openViewerForImage = useCallback(
    (imageId: string) => {
      const index = viewerItems.findIndex((item) => item.id === imageId);
      if (index >= 0) {
        setViewerIndex(index);
      }
    },
    [viewerItems],
  );

  // Close the viewer during render when its target disappears (item deleted /
  // list shrank) — derived clamp instead of a setState-in-effect.
  if (
    viewerIndex !== null &&
    (viewerItems.length === 0 || viewerIndex >= viewerItems.length)
  ) {
    setViewerIndex(null);
  }

  useEffect(() => {
    if (viewerIndex === null) {
      return;
    }
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setViewerIndex(null);
        return;
      }
      if (event.key === "ArrowLeft") {
        cycleViewer("prev");
      }
      if (event.key === "ArrowRight") {
        cycleViewer("next");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [cycleViewer, viewerIndex]);

  const addLocalImage = async (file: File) => {
    setUploadError(null);
    try {
      await validateImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      let added = false;
      setImageItems((current) => {
        if (current.length >= maxImages) {
          return current;
        }
        added = true;
        return [
          ...current,
          {
            id: createLocalId(),
            kind: "local",
            sortOrder: current.length,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            file,
            previewUrl,
          },
        ];
      });
      if (!added) {
        URL.revokeObjectURL(previewUrl);
      }
    } catch (caughtError) {
      const reason =
        caughtError instanceof Error ? caughtError.message : "invalidImage";
      if (reason === "fileTooLarge") {
        setUploadError(t(keys.errors.fileTooLarge));
        return;
      }
      setUploadError(t(keys.products.uploadFailed));
    }
  };

  const removeImage = (id: string) => {
    setImageItems((current) => {
      const target = current.find((item) => item.id === id);
      if (target?.previewUrl) {
        URL.revokeObjectURL(target.previewUrl);
      }
      return current.filter((item) => item.id !== id);
    });
  };

  const moveImage = (id: string, direction: "up" | "down") => {
    setImageItems((current) => moveImageByDirection(current, id, direction));
  };

  const setAsCover = (id: string) => {
    setImageItems((current) => moveImageToPosition(current, id, 0));
  };

  const handleBgRemovalVersionChange = useCallback(
    (imageId: string, version: "original" | "clean") => {
      setImageItems((current) =>
        current.map((item) => {
          if (item.id !== imageId || !item.bgRemoval) {
            return item;
          }
          return {
            ...item,
            bgRemoval: {
              ...item.bgRemoval,
              activeVersion: version,
            },
          };
        }),
      );
    },
    [],
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // The edit page submits every section form at once; skip this one when the
    // photos are unchanged so a clean section never triggers a no-op save.
    if (onSuccess && !hasUnsavedChanges) {
      return;
    }
    setError(null);
    setUploadError(null);
    setShowSuccess(false);
    setIsSubmitting(true);

    const pendingImages = orderedImages.filter(
      (image) => image.kind === "local",
    );
    let uploadedImages: UploadedImageMap = new Map();

    if (pendingImages.length > 0) {
      setIsUploading(true);
      try {
        uploadedImages = await uploadPendingImages({
          pendingImages,
          productId,
          createUploadIntent: createProductUploadIntent,
          uploadToSignedUrl,
        });
      } catch (caughtError) {
        if (
          typeof caughtError === "object" &&
          caughtError !== null &&
          "kind" in caughtError &&
          caughtError.kind === "request"
        ) {
          const requestMessage =
            "message" in caughtError && typeof caughtError.message === "string"
              ? caughtError.message
              : null;
          setError(requestMessage ?? t(keys.errors.generic));
        } else {
          setError(t(keys.errors.generic));
        }
        setUploadError(t(keys.products.uploadFailed));
        setIsSubmitting(false);
        setIsUploading(false);
        return;
      } finally {
        setIsUploading(false);
      }
    }

    const imagesPayload = buildImagesPayload(orderedImages, uploadedImages);

    if (imagesPayload.some((image) => image === null)) {
      setError(t(keys.products.uploadFailed));
      setIsSubmitting(false);
      return;
    }
    const normalizedImagesPayload = imagesPayload.filter(
      (
        image,
      ): image is {
        imageId: string;
        objectKey: string;
        sortOrder: number;
        useBgRemoved?: boolean;
      } => image !== null,
    );

    try {
      const updatedProduct = await updateProductImages({
        storeSlug,
        productId,
        images: normalizedImagesPayload,
      });

      const fallbackSavedImages = reconcileSavedImages(
        orderedImages,
        uploadedImages,
      );
      const refreshedImages = mapRemoteImages(updatedProduct.images);
      const nextSavedImages = refreshedImages.length
        ? mergeImagesWithPreviewFallback(refreshedImages, fallbackSavedImages)
        : fallbackSavedImages;
      setSavedImages(nextSavedImages);
      setImageItems(nextSavedImages);
      onImagesSaved?.(nextSavedImages.length);
      if (onSuccess) {
        // Caller owns the success feedback (shared toast on the edit page).
        onSuccess();
      } else {
        setShowSuccess(true);
        if (redirectOnSuccess) {
          navigate(`${getSellerPath(locale, storeSlug)}/products`, {
            replace: true,
          });
        }
      }
    } catch {
      setError(t(keys.errors.generic));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form
        id={formId}
        ref={formRef}
        onSubmit={handleSubmit}
        className="flex flex-col gap-5"
      >
        {showSuccess ? (
          <AutoDismissNotice
            title={successMessage ?? t(keys.seller.productSavedShare)}
            closeLabel={t(keys.common.close)}
          />
        ) : null}
        {error ? (
          <div ref={errorRef}>
            <Alert message={error} helperText={t(keys.errors.recovery)} />
          </div>
        ) : null}
        <ProductImagesHybridPanel
          orderedImages={orderedImages}
          maxImages={maxImages}
          imageLimitReached={imageLimitReached}
          isUploading={isUploading}
          isSubmitting={isSubmitting}
          disableReorder={disableReorder}
          uploadError={uploadError}
          fileInputRef={fileInputRef}
          showTips={showTips}
          onAddLocalImage={(file) => {
            void addLocalImage(file);
          }}
          onOpenViewer={openViewerForImage}
          onMoveImage={moveImage}
          onSetAsCover={setAsCover}
          onRemoveImage={removeImage}
          onBgRemovalVersionChange={handleBgRemovalVersionChange}
        />
        <ProductImagesViewer
          activeUrl={activeViewerUrl}
          viewerIndex={viewerIndex}
          totalItems={viewerItems.length}
          activeBgRemoval={activeViewerBgRemoval}
          onPrev={() => cycleViewer("prev")}
          onNext={() => cycleViewer("next")}
          onClose={() => setViewerIndex(null)}
        />
        {hideActions ? null : (
          <ProductImagesFormActions
            actionRef={actionRef}
            actionsVisible={actionsVisible}
            shouldFloatActions={shouldFloatActions}
            floatingVisible={floatingVisible}
            formId={formId}
            cancelLabel={t(keys.common.cancel)}
            saveLabel={saveLabel}
            loadingLabel={t(keys.common.loading)}
            onCancel={handleCancel}
            isSaving={isSubmitting}
            hideCancel={hideCancel}
          />
        )}
      </form>
      {/* {!guard ? (
        <DiscardChangesDialog
          {...discardDialogState}
          title={t(keys.forms.discardChangesTitle)}
          body={t(keys.forms.discardChangesBody)}
          discardLabel={t(keys.forms.discardChangesDiscard)}
          continueLabel={t(keys.forms.discardChangesContinue)}
        />
      ) : null} */}
    </>
  );
}
